/* eslint eqeqeq: "off", curly: "error", @typescript-eslint/no-unused-vars: "off" */
import React from 'react';
import DataTable from 'react-data-table-component';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Skeleton } from '@albo-ui/core';
import { Divider, Chip } from '@mui/material';
import { customStylesCompany } from '../../../util/TableStyles';
import { noDataComponent } from '../../../util/DataObject';
import { strategyLabel } from './strategies';
import moment from 'moment';

interface FeeDetailModalProps {
    open: boolean;
    data: any;
    loading: boolean;
    onClose: () => void;
    onPageChange: (page: number) => void;
}

const statusColor = (status: string) => {
    switch (status) {
        case 'FEE_APPLIED':
            return { bg: '#e0f2f1', color: '#2b9080' };
        case 'FEE_SKIPPED':
            return { bg: '#fdf3e0', color: '#c8922a' };
        case 'FEE_DISABLED':
            return { bg: '#efeafb', color: '#6a4fbd' };
        case 'ERROR':
        case 'FAILED':
            return { bg: '#ffebee', color: '#d32f2f' };
        default:
            return { bg: '#f5f5f5', color: '#666' };
    }
};

const detailColumns = [
    {
        name: 'Cliente',
        selector: (r: any) => r.customer_uuid,
        cell: (r: any) => <span style={{ fontSize: '0.8rem' }}>{r.customer_uuid}</span>,
        grow: 3,
        wrap: true,
        sortable: true,
    },
    {
        name: 'Estado',
        selector: (r: any) => r.status,
        cell: (r: any) => {
            const c = statusColor(r.status);
            return (
                <Chip
                    label={r.status}
                    size="small"
                    sx={{ backgroundColor: c.bg, color: c.color, fontWeight: 600, borderRadius: '16px', fontSize: '0.75rem' }}
                />
            );
        },
        grow: 1.5,
        sortable: true,
    },
    {
        name: 'Estrategia',
        selector: (r: any) => r.fee_type,
        cell: (r: any) => <span style={{ fontSize: '0.8rem' }}>{strategyLabel(r.fee_type)}</span>,
        grow: 1.5,
        wrap: true,
        sortable: true,
    },
    {
        name: 'Aplica fee',
        selector: (r: any) => String(r.apply_fee),
        cell: (r: any) => <span>{r.apply_fee ? 'Sí' : 'No'}</span>,
        grow: 1,
        center: true,
        sortable: true,
    },
    {
        name: 'Procesado',
        selector: (r: any) => r.processed_at || r.cdate,
        cell: (r: any) => <span>{r.processed_at ? moment(r.processed_at).format('DD/MM/YYYY HH:mm') : '-'}</span>,
        grow: 1.5,
        sortable: true,
    },
];

/* Cada fila es expandible: muestra el objeto completo (todos los campos) */
const DetailExpanded = ({ data }: any) => (
    <pre
        style={{
            width: '100%',
            background: '#f9f9f9',
            padding: 12,
            borderRadius: 8,
            overflow: 'auto',
            fontSize: '0.75rem',
            margin: '8px 0',
            maxHeight: 400,
            boxSizing: 'border-box',
        }}
    >
        {JSON.stringify(data, null, 2)}
    </pre>
);

export const FeeDetailModal: React.FC<FeeDetailModalProps> = ({ open, data, loading, onClose, onPageChange }) => {
    const details = data?.details ?? [];
    const pagination = data?.pagination ?? {};

    return (
        <Dialog open={open} onClose={onClose} style={{ minWidth: '1000px', maxHeight: '90vh' }}>
            <DialogTitle>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    Detalle de ejecución{data?.execution_id ? `: ${data.execution_id}` : ''}
                </span>
            </DialogTitle>

            <Divider />

            <DialogContent style={{ maxHeight: '70vh', overflow: 'auto' }}>
                <Typography variant="smallTitle" color="darkGray" style={{ display: 'block', marginBottom: 10 }}>
                    Total de registros: {(pagination?.total ?? 0).toLocaleString('es-MX')}
                </Typography>
                {loading ? (
                    <Skeleton width="100%" height="300px" />
                ) : (
                    <DataTable
                        columns={detailColumns as any}
                        data={details}
                        customStyles={customStylesCompany}
                        noDataComponent={noDataComponent}
                        expandableRows
                        expandableRowsComponent={DetailExpanded}
                        pagination
                        paginationServer
                        paginationTotalRows={pagination?.total ?? 0}
                        paginationDefaultPage={pagination?.page_number ?? 1}
                        paginationPerPage={pagination?.results_per_page ?? 50}
                        paginationRowsPerPageOptions={[50]}
                        onChangePage={onPageChange}
                    />
                )}
            </DialogContent>

            <Divider />

            <DialogActions>
                <Button buttonType="secondary" onClick={onClose} size="normal">
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
};